import { useEffect, useState, useCallback, useRef } from 'react'
import axios, { AxiosResponse } from 'axios'

type ApiDataState<TData> = {
  data: TData | null
  isLoading: boolean
  error: string | null
  // HTTP status of a failed request, when there was one. Lets callers tell a
  // 404 (show the "not found" page) apart from a 500/network error (retry).
  status: number | null
}

type UseApiDataOptions = {
  errorMessage?: string
  enabled?: boolean
}

type UseApiDataResult<TData> = ApiDataState<TData> & {
  refetch: () => void
}

/**
 * Generic hook for fetching data from an API endpoint
 *
 * @param fetchFunction - Async function that returns an AxiosResponse
 * @param dependencies - Array of dependencies that trigger a refetch when changed
 * @param options - Optional configuration (custom error message, enabled flag)
 *
 * @example
 * const { data, isLoading, error } = useApiData(
 *   () => moviesApi.getById(movieId),
 *   [movieId],
 *   { errorMessage: 'Failed to load movie details' }
 * )
 */
export function useApiData<TData>(
  fetchFunction: () => Promise<AxiosResponse<TData>>,
  dependencies: unknown[],
  options: UseApiDataOptions = {}
): UseApiDataResult<TData> {
  const { errorMessage = 'Failed to load data', enabled = true } = options

  const [state, setState] = useState<ApiDataState<TData>>({
    data: null,
    isLoading: true,
    error: null,
    status: null,
  })

  const isMountedRef = useRef(true)

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setState((previousState) => ({ ...previousState, isLoading: false }))
      return
    }

    setState((previousState) => ({ ...previousState, isLoading: true, error: null, status: null }))

    try {
      const response = await fetchFunction()
      if (isMountedRef.current) {
        setState({ data: response.data, isLoading: false, error: null, status: null })
      }
    } catch (err) {
      if (isMountedRef.current) {
        const status = axios.isAxiosError(err) ? err.response?.status ?? null : null
        setState({ data: null, isLoading: false, error: errorMessage, status })
      }
    }
  }, [fetchFunction, errorMessage, enabled])

  useEffect(() => {
    isMountedRef.current = true
    fetchData()

    return () => {
      isMountedRef.current = false
    }
  }, dependencies) // eslint-disable-line react-hooks/exhaustive-deps

  const refetch = useCallback(() => {
    fetchData()
  }, [fetchData])

  return { ...state, refetch }
}

export default useApiData
